import axios from 'axios';
import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';
import { ConfigurationInterface } from '@e3labs/isa-data-dictionary';

import * as bonificationProductActions from './bonification-product-actions';
import bonificationProductService from './bonification-product-service';

import {
  DELETE_ITEM,
  EDIT_ITEM,
  GET_BY_ID_REQUEST,
  GET_LIST_BONIFICATION_PRODUCT_REQUEST,
  GET_PRODUCTS_REQUEST,
  SAVE_BONIFICATION_PRODUCT_REQUEST
} from './bonification-product-constants';

import { ISA_ERROR } from '~/constants';

import { AppStateType } from '../root-reducer';

import { formatMessage } from '~/i18n/translate';

import history from '~/services/history';
import notification from '~/services/notification';
import domainService from '~/services/modules/domain/domain-service';
import api from '~/services/api';
import productService from '../product/product-service';

let cancel: any;
const CancelToken = axios.CancelToken;

const INITIAL_STATE = {
  teams: [],
  restriction: '',
  users: [],
  superiors: [],
  products: [],
  categories: [],
  sections: [],
  providers: [],
  brands: [],
  productLines: [],
  subsidiaries: []
};

export function* getAllBonificationProductList() {
  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(bonificationProductActions.setLoadingData(true));

    const { rowsPerPage, page, order, orderBy } = yield select((state: AppStateType) => state.bonificationProduct.list);

    const pagination = {
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const [{ data: bonificationProducts }, { data: count }] = yield all([
      call(
        bonificationProductService.getBonificationProductList,
        {
          ...pagination
        },
        {
          cancelToken
        }
      ),
      call(bonificationProductService.getListCount, { ...pagination }, { cancelToken })
    ]);

    yield put(
      bonificationProductActions.getAllBonificationProductSuccess({
        rows: bonificationProducts,
        rowsCount: count?.count
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(bonificationProductActions.setLoadingData(false));
  }
}

export function* getById({ id }: any) {
  try {
    yield put(bonificationProductActions.setLoadingData(true));
    const user = yield select((state: AppStateType) => state.user.user);

    const [
      { data: categories },
      { data: sections },
      { data: providers },
      { data: brands },
      { data: productLines },
      { data: teams },
      { data: products },
      { data: users },
      { data: subsidiaries }
    ] = yield all([
      call(domainService.getCategories),
      call(domainService.getSections),
      call(domainService.getProviders),
      call(domainService.getBrands),
      call(domainService.getProductLines),
      call(api.get, '/team'),
      call(productService.getRawProducts, {
        skip: 0,
        limit: 50
      }),
      call(bonificationProductService.getSellers, {}),
      call(domainService.getSubsidiaries, { userSubsidiaryFilter: user?.userSubsidiaries })
    ]);

    const selects = {
      subsidiaries,
      brands,
      categories,
      providers,
      sections,
      productLines,
      teams,
      products,
      users: users?.filter(user => user?.userRole === 'vendor'),
      superiors: users?.filter(user => user?.userRole !== 'vendor')
    };

    yield put(bonificationProductActions.getSelectsSuccess(selects));

    if (id) {
      const { data: bonification } = yield call(bonificationProductService.getByIdBonificationProduct, id);

      const { data: bonificationProducts } = yield call(productService.getRawProducts, {
        skip: 0,
        limit: 100,
        productExternalCodes: bonification.productExternalCodes?.join(',')
      });

      const parsedBonification = {
        restriction: bonification.restriction,
        users: users.filter(item => bonification.userExternalCodes.includes(item.externalCode)),
        superiors: users.filter(item => bonification.superiorExternalCodes.includes(item.externalCode)),
        categories: categories.filter(item => bonification.categoryExternalCodes.includes(item.externalCode)),
        sections: sections.filter(item => bonification.sectionExternalCodes.includes(item.externalCode)),
        providers: providers.filter(item => bonification.providerExternalCodes.includes(item.externalCode)),
        brands: brands.filter(item => bonification.brandExternalCodes.includes(item.externalCode)),
        productLines: productLines.filter(item => bonification.productLinesExternalCodes.includes(item.externalCode)),
        teams: teams.filter(item => bonification.teams.includes(item._id)),
        products: bonificationProducts,
        subsidiaries: subsidiaries.filter(item => bonification.subsidiaryExternalCodes.includes(item.externalCode))
      };

      yield put(bonificationProductActions.getByIdSuccess(parsedBonification));
    } else {
      yield put(bonificationProductActions.getByIdSuccess(INITIAL_STATE));
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(bonificationProductActions.setLoadingData(false));
  }
}

export function* saveBonificationProduct({ payload, id }: any) {
  try {
    yield put(bonificationProductActions.setLoadingSend(true));

    const parsedPayload = {
      userExternalCodes: payload?.users?.map(user => user.externalCode),
      superiorExternalCodes: payload?.superiors?.map(superior => superior.externalCode),
      productExternalCodes: payload?.products?.map(product => product.externalCode),
      categoryExternalCodes: payload?.categories?.map(category => category.externalCode),
      sectionExternalCodes: payload?.sections?.map(section => section.externalCode),
      providerExternalCodes: payload?.providers?.map(provider => provider.externalCode),
      brandExternalCodes: payload?.brands?.map(brand => brand.externalCode),
      productLinesExternalCodes: payload?.productLines?.map(productLine => productLine.externalCode),
      subsidiaryExternalCodes: payload?.subsidiaries?.map(subsidiary => subsidiary.externalCode),
      teams: payload?.teams?.map(team => team._id),
      restriction: payload?.restriction
    };

    if (id) {
      yield call(bonificationProductService.editBonificationProduct, {
        id,
        data: {
          ...parsedPayload
        }
      });
    } else {
      yield call(bonificationProductService.saveBonificationProduct, {
        ...parsedPayload
      });
    }

    yield call(
      notification.success,
      id ? formatMessage('productBonificationUpdatedSuccess') : formatMessage('productBonificationCreatedSuccess')
    );

    yield call(history.push, `/register/product-bonification`);
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(bonificationProductActions.setLoadingSend(false));
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/register/product-bonification/edit/${id}`);
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('productBonificationExcludeDeleteMessage')
    );
    if (isConfirmed) {
      yield put(bonificationProductActions.setLoadingSend(true));
      yield call(bonificationProductService.deleteBonificationProduct, id);
      yield put(bonificationProductActions.setLoadingSend(false));

      yield put(bonificationProductActions.getAllBonificationProductRequest());
      yield call(notification.success, formatMessage('productBonificationExcludeSuccess'));
    }
  } catch (error) {
    yield put(bonificationProductActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getProducts({ queryString, skip }: any) {
  yield delay(200);

  if (cancel !== undefined) cancel();

  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  const { selects } = yield select((state: AppStateType) => state.bonificationProduct.form);
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

  try {
    yield put(bonificationProductActions.setLoadingSelects(true));

    const params = {
      skip: skip === 0 ? 0 : skip,
      limit: 25,
      queryString,
      searchEngine: configuration.searchEngine
    };

    const { data: products } = yield call(
      domainService.getProductsMinimized,
      {
        ...params
      },
      {
        cancelToken
      }
    );

    const existingProductIds = selects?.products?.map(product => product?.externalCode);
    const filteredProducts = products?.filter(product => !existingProductIds?.includes(product?.externalCode));
    const parsedProducts =
      [...selects?.products, ...filteredProducts]?.sort((a, b) => a?.name.localeCompare(b?.name)) ?? [];

    yield put(bonificationProductActions.getProductsSuccess(parsedProducts));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(bonificationProductActions.setLoadingSelects(false));
  }
}

export default all([
  takeLatest(GET_LIST_BONIFICATION_PRODUCT_REQUEST, getAllBonificationProductList),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_BONIFICATION_PRODUCT_REQUEST, saveBonificationProduct),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(GET_PRODUCTS_REQUEST, getProducts)
]);
